// src/pages/SimpleAssistant.jsx
import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  LiveKitRoom,
  useVoiceAssistant,
  BarVisualizer,
  RoomAudioRenderer,
  VoiceAssistantControlBar,
  useRoomContext,
} from "@livekit/components-react";
import { useKrispNoiseFilter } from "@livekit/components-react/krisp";
import { supabase } from "../supabaseClient";
import { AuthContext } from "../contexts/AuthContext";
import "@livekit/components-styles";
import { NoAgentNotification } from "../components/NoAgentNotification";
import { trackDebugEvent } from "../loggerService";
import { TranscriptionTile } from "../components/TranscriptionTile";
import { Bounce, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Define a simple theme for the assistant
const assistantTheme = {
  "--lk-bg": "#ffffff",
  "--lk-bg-secondary": "#ffffff",
  "--lk-fg": "#000000",
  "--lk-fg-secondary": "#333333",
  "--lk-accent-bg": "#0a8a6d",
  "--lk-accent-fg": "#ffffff",
};

/**
 * DisconnectOnUnmount
 *
 * Leverages cleanup to disconnect from the LiveKit room when the component unmounts.
 */
function DisconnectOnUnmount() {
  const { room } = useRoomContext();
  const wasConnectedRef = useRef(false);

  useEffect(() => {
    if (room?.connectionState === "connected") {
      console.log("YEEEEY Connected to room", room);
      wasConnectedRef.current = true;
    }
  }, [room?.connectionState]);

  useEffect(() => {
    return () => {
      if (room && wasConnectedRef.current) {
        setTimeout(() => {
          if (room.connectionState === "connected") {
            console.log("YEEEEY Disconnecting from room on unmount", room);
            room.disconnect();
          }
        }, 100);
      }
    };
  }, [room]);

  return null;
}

/**
 * SimpleVoiceAssistant
 *
 * Renders a bar visualizer reflecting voice assistant state.
 */
function SimpleVoiceAssistant({ onStateChange, onAudioTrack }) {
  const { state, audioTrack } = useVoiceAssistant();
  const prevStateRef = useRef(JSON.stringify(state));

  useEffect(() => {
    const currentState = JSON.stringify(state);
    if (prevStateRef.current !== currentState) {
      prevStateRef.current = currentState;
      onStateChange(state);
      onAudioTrack(audioTrack);
    }
  }, [state, onStateChange, audioTrack]);

  return (
    <div className="h-[300px] max-w-[90vw] mx-auto">
      <BarVisualizer
        state={state}
        barCount={5}
        trackRef={audioTrack}
        className="agent-visualizer"
        options={{ minHeight: 24 }}
      />
    </div>
  );
}

/**
 * ControlBar
 *
 * Renders a button to start the conversation and displays the voice assistant control bar.
 */
function ControlBar({ onConnectButtonClicked, agentState, autoStart }) {
  const { setNoiseFilterEnabled } = useKrispNoiseFilter();
  const isKrispInitialized = useRef(false);

  useEffect(() => {
    if (!isKrispInitialized.current) {
      setNoiseFilterEnabled(true);
      isKrispInitialized.current = true;
    }
  }, [setNoiseFilterEnabled]);

  return (
    <div className="relative h-[100px] flex items-center justify-center">
      <AnimatePresence>
        {agentState === "disconnected" && !autoStart && (
          <motion.button
            initial={{ opacity: 0, top: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, top: "-10px" }}
            transition={{ duration: 1, ease: [0.09, 1.04, 0.245, 1.055] }}
            className="uppercase px-4 py-2 rounded-full shadow bg-green-600 text-white hover:bg-green-700 transition-colors"
            onClick={onConnectButtonClicked}
          >
            Start a conversation
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {agentState !== "disconnected" && agentState !== "connecting" && (
          <motion.div
            initial={{ opacity: 0, top: "10px" }}
            animate={{ opacity: 1, top: 0 }}
            exit={{ opacity: 0, top: "-10px" }}
            transition={{ duration: 0.4, ease: [0.09, 1.04, 0.245, 1.055] }}
            className="flex items-center gap-3 backdrop-blur-sm px-3 py-2 rounded-lg"
          >
            <VoiceAssistantControlBar controls={{ leave: false }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * AssistantInner
 *
 * This component is rendered inside LiveKitRoom, ensuring that room context is available.
 * It exposes a disconnect() method via ref.
 */
const AssistantInner = forwardRef(
  (
    { autoStart, agentState, onStateChange, onAudioTrack, onConnectButtonClicked },
    ref
  ) => {
    const { room } = useRoomContext();
    const wasConnectedRef = useRef(false);

    useEffect(() => {
      if (room?.connectionState === "connected") {
        wasConnectedRef.current = true;
      }
    }, [room?.connectionState]);

    useImperativeHandle(ref, () => ({
      disconnect: () => {
        if (room && room.connectionState === "connected") {
          console.log("Manual disconnect triggered");
          room.disconnect();
        }
      },
    }));

    return (
      <>
        <DisconnectOnUnmount />
        <SimpleVoiceAssistant onStateChange={onStateChange} onAudioTrack={onAudioTrack} />
        <ControlBar
          onConnectButtonClicked={onConnectButtonClicked}
          agentState={agentState}
          autoStart={autoStart}
        />
        <RoomAudioRenderer />
        {/* Pass dynamic agentState instead of a fixed string */}
        <NoAgentNotification state={agentState} />
      </>
    );
  }
);

/**
 * SimpleAssistantPage
 *
 * This ties everything together: connecting to the LiveKit room, handling errors,
 * and exposing disconnect(). Duplicate error toasts are prevented with refs.
 */
const SimpleAssistantPage = forwardRef(function SimpleAssistantPage(
  { autoStart = false },
  ref
) {
  const { user, profileLoaded } = useContext(AuthContext);
  const [connectionDetails, setConnectionDetails] = useState(undefined);
  const [agentState, setAgentState] = useState("disconnected");
  const [agentAudioTrack, setAgentAudioTrack] = useState(null);
  
  // Flags to prevent duplicate errors
  const micErrorShownRef = useRef(false);
  const tokenErrorShownRef = useRef(false);
  const tokenFetchAttemptedRef = useRef(false);

  // Connect button handler
  const onConnectButtonClicked = useCallback(async () => {
    trackDebugEvent("onConnectButtonClicked", {
      platform: navigator.platform,
      userId: user?.id,
    });

    if (!user || !profileLoaded) {
      trackDebugEvent("connect_failed", {
        reason: "User not logged in or profile not loaded",
      });
      console.log("User not logged in", user, profileLoaded);
      return;
    }

    try {
      trackDebugEvent("request_mic_permission", { message: "Attempting getUserMedia" });
      await navigator.mediaDevices.getUserMedia({ audio: true });
      trackDebugEvent("mic_permission_granted", { message: "getUserMedia succeeded" });
    } catch (permissionErrorObj) {
      trackDebugEvent("mic_permission_denied", { error: permissionErrorObj.message });
      console.error("getUserMedia error:", permissionErrorObj);
      if (!micErrorShownRef.current) {
        toast.error("Microphone permission denied. Please allow access and try again.", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        micErrorShownRef.current = true;
      }
      return;
    }

    // Attempt token fetch only once
    // if (!tokenFetchAttemptedRef.current) {
    if (!connectionDetails) {
      tokenFetchAttemptedRef.current = true;
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        trackDebugEvent("apiClient_session_error", { error: error.message });
        console.log("apiClient_session_error", { error: error.message });
        toast.error(
          "Oops! We encountered a problem connecting to the API. Please try again later.",
          {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          }
        );
        return;
      }

      const defaultHeaders = { "Content-Type": "application/json" };
      if (session?.access_token) {
        defaultHeaders.Authorization = `Bearer ${session.access_token}`;
      }
      const url = new URL(
        "/livekit/getToken",
        process.env.REACT_APP_API_URL || window.location.origin
      );
      url.searchParams.append("identity", user.id);

      try {
        const response = await fetch(url.toString(), {
          method: "GET",
          headers: defaultHeaders,
        });
        const data = await response.json();
        setConnectionDetails(data);
        trackDebugEvent("token_received", { connectionDetails: data });
      } catch (fetchError) {
        trackDebugEvent("fetch_error", { error: fetchError.message });
        console.error("Fetch error:", fetchError);
        if (!tokenErrorShownRef.current) {
          toast.error(
            "Oops! We encountered a problem connecting to the API. Please try again later.",
            {
              position: "top-right",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
              transition: Bounce,
            }
          );
          tokenErrorShownRef.current = true;
        }
      }
    }
  }, [user, profileLoaded, connectionDetails]);

  // Auto-connect if autoStart is true.
  useEffect(() => {
    if (autoStart && agentState === "disconnected") {
      onConnectButtonClicked();
      // console.log("YEEEY   Auto-starting conversation");
    }
  }, [autoStart, agentState, onConnectButtonClicked]);

  // Create a ref for AssistantInner.
  const assistantInnerRef = useRef(null);

  // Expose a disconnect() method via ref.
  useImperativeHandle(ref, () => ({
    disconnect: () => {
      if (assistantInnerRef.current) {
        assistantInnerRef.current.disconnect();
      }
    },
  }));

  return (
    <div className="h-full w-full grid content-center bg-white pt-8">
      <div data-lk-theme="default" className="w-full h-full" style={assistantTheme}>
        <LiveKitRoom
          token={connectionDetails?.participantToken}
          serverUrl={connectionDetails?.serverUrl}
          connect={connectionDetails !== undefined}
          audio={true}
          video={false}
          onMediaDeviceFailure={(error) => {
            trackDebugEvent("onDeviceFailure", { error: error.message });
            console.error(error);
            // Show toast only if not already handled and for non-permission errors.
            if (!micErrorShownRef.current && error.name !== "NotAllowedError") {
              toast.error(
                "Error acquiring microphone permissions. Please ensure you grant the necessary permissions and reload the page.",
                {
                  position: "top-right",
                  autoClose: 2000,
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: true,
                  draggable: true,
                  progress: undefined,
                  theme: "light",
                  transition: Bounce,
                }
              );
              micErrorShownRef.current = true;
            }
          }}
          onDisconnected={() => {
            trackDebugEvent("onDisconnected", { message: "Connection lost" });
            setConnectionDetails(undefined);
          }}
          className="grid grid-rows-[2fr_1fr] items-center"
        >
          <AssistantInner
            ref={assistantInnerRef}
            autoStart={autoStart}
            agentState={agentState}
            onStateChange={setAgentState}
            onAudioTrack={setAgentAudioTrack}
            onConnectButtonClicked={onConnectButtonClicked}
          />
          {agentAudioTrack && (
            <TranscriptionTile agentAudioTrack={agentAudioTrack} accentColor="#0a8a6d" />
          )}
        </LiveKitRoom>
      </div>
    </div>
  );
});

export default SimpleAssistantPage;
